import type { JSX, ReactNode } from 'react';
import { EyeOff } from 'lucide-react';

import { RESTRICTED_FIELD_TEXT } from '@/features/guardian/labels';
import { cn } from '@/lib/utils';

/**
 * The one reusable presentation for a field a LIMITED_READ Guardian
 * relationship may have redacted to `null` (ADR-025's Addendum,
 * "LIMITED_READ Field Redaction Policy"). Use ONLY at a call site that
 * already knows a `null` here can mean "restricted", never as a generic
 * null-to-text converter - a field's `null` can also mean "genuinely
 * absent" in plenty of other contexts, and this component must not be
 * reached for those. Never renders a restricted score as `0` or as a
 * plain `-`, either of which would misleadingly read as missing data
 * rather than access-restricted data.
 */
export function RestrictedField({
  value,
  render,
  className,
}: {
  readonly value: string | null;
  readonly render: (value: string) => ReactNode;
  readonly className?: string;
}): JSX.Element {
  if (value === null) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 text-sm text-muted-foreground italic',
          className,
        )}
      >
        <EyeOff className="size-3.5" aria-hidden="true" />
        {RESTRICTED_FIELD_TEXT}
      </span>
    );
  }
  return <>{render(value)}</>;
}
